import { useState } from 'react';
import { useFieldArray } from 'react-hook-form';

import { DatamodelFormField } from '@Forms/fields.jsx';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, TrashIcon } from '@svgs';

const blankObject = (datamodel) => {
  // generate a blank object for a datamodel using its field slugs
  const defaultValues = datamodel.fields.map((field) => {
    let vals = {
      [field.slug]: null,
    };
    if (field.field_type == 'image') {
      // add blank ID
      vals[`${field.slug}_id`] = null;
    }
    return vals;
  });

  let blankValues = {};
  defaultValues.forEach((v) => {
    blankValues = {
      ...blankValues,
      ...v,
    };
  });
  return blankValues;
};

const RelatedModelItem = ({
  fieldArrayReturn,
  fieldName,
  index,
  isUploading,
  relatedDataModel,
  showConfirmation,
  setIsUploading,
  setShowConfirmation,
  siteID,
  useFormReturn,
  maxItems,
  minItems,
  isRequired,
}) => {
  const { fields, append, remove, move, insert } = fieldArrayReturn;
  const subFieldsList = relatedDataModel.fields.map((f) => {
    // fieldPrefix is passed using the related model slug, index and the slug from the field
    // this pattern is recognized by react hook forms and will auto-serialize it into lists
    return (
      <DatamodelFormField
        key={`${fieldName}.${index}.${f.slug}`}
        fieldPrefix={`${fieldName}.${index}.`}
        datamodelField={f}
        setIsUploading={setIsUploading}
        siteID={siteID}
        useFormReturn={useFormReturn}
      />
    );
  });

  return (
    <div className="sb-form-item-inner">
      <div className="sb-form-item-elements">
        <fieldset>{subFieldsList}</fieldset>
      </div>
      {showConfirmation != index + 1 && (
        <div className="sb-form-item-controls">
          <>
            <button
              type="button"
              disabled={index === 0 || isUploading}
              onClick={() => {
                setShowConfirmation(false);
                move(index, index - 1);
              }}
              className="sb-form-item-control sb-form-item-control-up"
            >
              <ChevronUpIcon />
            </button>
            <button
              type="button"
              disabled={index === fields.length - 1 || isUploading}
              onClick={() => {
                setShowConfirmation(false);
                move(index, index + 1);
              }}
              className="sb-form-item-control sb-form-item-control-down"
            >
              <ChevronDownIcon />
            </button>

            <button
              type="button"
              disabled={isUploading}
              onClick={() => setShowConfirmation(index + 1)}
              className="sb-form-item-control sb-form-item-control-delete"
            >
              <TrashIcon />
            </button>
            <button
              type="button"
              disabled={isUploading}
              onClick={() => {
                return insert(index + 1, blankObject(relatedDataModel));
              }}
              className="sb-form-item-control sb-form-item-control-add"
            >
              <PlusIcon />
            </button>
          </>
        </div>
      )}
      {showConfirmation == index + 1 && (
        <div className="dialog">
          <h3>Remove Item?</h3>
          <button
            type="button"
            className="sb-btn sb-btn-save"
            onClick={() => {
              setShowConfirmation(false);
              remove(index);
            }}
          >
            Confirm
          </button>
          <button
            type="button"
            className="sb-btn sb-btn-cancel"
            onClick={() => setShowConfirmation(false)}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export const RelatedModelList = ({
  datamodelField,
  fieldName,
  relatedDataModel,
  siteID,
  useFormReturn,
}) => {
  // unpack form vars
  const { register, control } = useFormReturn;

  const [showConfirmation, setShowConfirmation] = useState(false);

  // state functions for tracking if images are being uploaded within the group
  const [isUploading, setIsUploading] = useState(false);

  const fieldArrayReturn = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: fieldName, // unique name for your Field Array
  });
  const { fields, append, remove, move, insert } = fieldArrayReturn;

  const maxItems = datamodelField.max_items;
  const minItems = datamodelField.min_items;
  const isRequired = datamodelField.required;

  const fieldsList = fields.map((field, index) => {
    return (
      <RelatedModelItem
        key={field.id || `${fieldName}.${index}`}
        fields={fields}
        fieldName={fieldName}
        fieldArrayReturn={fieldArrayReturn}
        index={index}
        isUploading={isUploading}
        setIsUploading={setIsUploading}
        relatedDataModel={relatedDataModel}
        showConfirmation={showConfirmation}
        setShowConfirmation={setShowConfirmation}
        siteID={siteID}
        useFormReturn={useFormReturn}
        //
        maxItems={maxItems}
        minItems={minItems}
        isRequired={isRequired}
      />
    );
  });

  return (
    <>
      {fieldsList}
      <button
        type="button"
        className="sb-btn sb-btn-cancel"
        onClick={() => append(blankObject(relatedDataModel))}
      >
        Add new item
      </button>
    </>
  );
};
